import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

/**
 * Settings → Newsletter · MailerLite → who the campaign goes to, and in
 * what language.
 *
 * Replaces the old single-group `<select>`. Two modes:
 *   • All subscribers — the campaign carries no group filter at all.
 *   • Specific groups — one or more groups, checked from the live list.
 *
 * The language selects MailerLite's `language_id`, which is what decides
 * the language of the hosted unsubscribe page. It has to be looked up
 * per account rather than hardcoded, so the options come from the API.
 *
 * Props are the three draft fields plus the token flag; `onChange`
 * patches the draft the same way the rest of SettingsPage does.
 */
export default function MailerLiteAudience({
  allSubscribers,
  groupIds = [],
  language,
  legacyGroupId = "",
  tokenSaved,
  onChange,
}) {
  const {
    data: groups, isLoading, error, refetch, isFetching,
  } = useQuery({
    queryKey: ["mailerlite-groups"],
    queryFn: () => api.newsletterGroups(),
    enabled: !!tokenSaved,
    staleTime: 60_000,
    retry: false,
  });

  const { data: languages } = useQuery({
    queryKey: ["mailerlite-languages"],
    queryFn: () => api.newsletterLanguages(),
    enabled: !!tokenSaved,
    staleTime: 24 * 60 * 60_000,
    retry: false,
  });

  // A property configured before multi-group existed still has its id in
  // the legacy field; show it as checked so nothing looks lost.
  const selected = groupIds.length
    ? groupIds
    : (legacyGroupId ? [String(legacyGroupId)] : []);

  const toggleGroup = (id) => {
    const key = String(id);
    const next = selected.includes(key)
      ? selected.filter((g) => g !== key)
      : [...selected, key];
    onChange({ mailerlite_group_ids: next, mailerlite_group_id: next[0] || "" });
  };

  const errDetail = error?.response?.data?.detail || error?.message || "";
  const labelCls =
    "text-xs uppercase tracking-wider text-[var(--ink-muted)] font-mono-display";
  const inputCls =
    "w-full mt-1 px-3 py-2 text-sm bg-white border border-[var(--border)] outline-none focus:border-[var(--ink)]";

  return (
    <div className="space-y-3">
      <div>
        <span className={labelCls}>Send to</span>
        <div className="flex flex-wrap gap-4 mt-1.5">
          {[
            ["all", "All subscribers", true],
            ["groups", "Specific groups", false],
          ].map(([key, label, val]) => (
            <label key={key} className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="ml-audience"
                data-testid={`ml-audience-${key}`}
                checked={!!allSubscribers === val}
                onChange={() => onChange({ mailerlite_all_subscribers: val })}
              />
              <span className="text-sm text-[var(--ink)]">{label}</span>
            </label>
          ))}
        </div>
        {allSubscribers && (
          <p className="text-[11px] text-[var(--ink-muted)] mt-1">
            The campaign goes out with no group filter — everyone active
            in this MailerLite account receives it.
          </p>
        )}
      </div>

      {!allSubscribers && (
        <div>
          <label className={`${labelCls} flex items-center gap-2`}>
            Subscriber groups
            {tokenSaved && (
              <button
                type="button"
                data-testid="mailerlite-group-refresh"
                onClick={() => refetch()}
                className="normal-case tracking-normal text-[10px] text-[var(--accent)] hover:underline"
              >
                {isFetching ? "refreshing…" : "refresh"}
              </button>
            )}
          </label>

          {!tokenSaved ? (
            <>
              <input
                data-testid="mailerlite-group-input"
                value={selected[0] || ""}
                onChange={(e) =>
                  onChange({
                    mailerlite_group_ids: e.target.value ? [e.target.value] : [],
                    mailerlite_group_id: e.target.value,
                  })
                }
                placeholder="Save the API token first to auto-load groups"
                className={inputCls}
              />
              <p className="text-[11px] text-[var(--ink-muted)] mt-1">
                Save your API token above and this turns into a live,
                multi-select list of your MailerLite groups.
              </p>
            </>
          ) : isLoading ? (
            <div className="mt-1 px-3 py-2 text-sm text-[var(--ink-muted)] border border-[var(--border)] bg-white">
              Loading groups from MailerLite…
            </div>
          ) : error ? (
            <>
              <div
                data-testid="mailerlite-group-error"
                className="mt-1 px-3 py-2 text-xs bg-red-50 border border-red-200 text-red-800"
              >
                Couldn&apos;t load groups: {errDetail || "unknown error"}
              </div>
              <input
                data-testid="mailerlite-group-input"
                value={selected[0] || ""}
                onChange={(e) =>
                  onChange({
                    mailerlite_group_ids: e.target.value ? [e.target.value] : [],
                    mailerlite_group_id: e.target.value,
                  })
                }
                placeholder="Numeric group ID (manual fallback)"
                className={inputCls}
              />
            </>
          ) : (groups || []).length === 0 ? (
            <p className="text-[11px] text-amber-700 mt-1">
              No subscriber groups found — create one in your MailerLite
              dashboard first, then hit refresh.
            </p>
          ) : (
            <div
              data-testid="mailerlite-group-list"
              className="mt-1 border border-[var(--border)] bg-white max-h-56 overflow-y-auto divide-y divide-[var(--border)]"
            >
              {groups.map((g) => (
                <label
                  key={g.id}
                  className="flex items-center gap-2 px-3 py-2 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    data-testid={`ml-group-${g.id}`}
                    checked={selected.includes(String(g.id))}
                    onChange={() => toggleGroup(g.id)}
                  />
                  <span className="text-sm text-[var(--ink)]">{g.name}</span>
                </label>
              ))}
            </div>
          )}

          {!allSubscribers && selected.length === 0 && tokenSaved && !isLoading && (
            <p className="text-[11px] text-amber-700 mt-1">
              Pick at least one group — a newsletter can&apos;t be drafted
              or sent without recipients.
            </p>
          )}
          {selected.length > 1 && (
            <p className="text-[11px] text-[var(--ink-muted)] mt-1">
              {selected.length} groups selected. Someone in two of them
              still receives the email once.
            </p>
          )}
        </div>
      )}

      <div>
        <label className={labelCls}>Unsubscribe page language</label>
        <select
          data-testid="mailerlite-language"
          value={language || "en"}
          onChange={(e) => onChange({ mailerlite_language: e.target.value })}
          className={inputCls}
        >
          {(languages || []).length === 0 ? (
            <option value={language || "en"}>English</option>
          ) : (
            languages.map((l) => (
              <option key={l.shortcode} value={l.shortcode}>
                {l.name}
              </option>
            ))
          )}
        </select>
        <p className="text-[11px] text-[var(--ink-muted)] mt-1">
          MailerLite hosts the unsubscribe page; this sets the language it
          renders in.
        </p>
      </div>
    </div>
  );
}